/**
 * SwipaLoader — brand motion language.
 *
 * Three chevrons with staggered opacity + translateY → a continuous
 * "swipe-up wave".  This is the animated counterpart of SwipaLogo (2
 * chevrons, static).  Used for media uploads, send-in-flight, splash
 * screens, pull-to-refresh etc.  Color defaults to brand burgundy.
 *
 * Spec from swipa design bundle (logos-final.jsx: Loader component):
 *   viewBox 120×120
 *   strokeWidth 11, stroke-linecap/linejoin round
 *   three paths at y=40, y=64, y=88 (top peak at y=20, 44, 68)
 *   animation 1.1s cubic-bezier, stagger 0.18s
 *   keyframes sw-trail:
 *     0%   opacity .12  translateY(6px)
 *     30%  opacity 1    translateY(0)
 *     60%  opacity .6   translateY(-2px)
 *     100% opacity .12  translateY(-8px)
 */
import { useId } from 'react';

type Mode = 'inline' | 'splash' | 'overlay' | 'bubble';

export interface SwipaLoaderProps {
  mode?: Mode;
  size?: number;
  color?: string;
  speed?: number;          // seconds per loop, default 1.1
  label?: string;
  bg?: string;             // for bubble/overlay modes
}

const BURGUNDY = '#7A1F2B';

export function SwipaLoader({
  mode = 'inline',
  size = 64,
  color = BURGUNDY,
  speed = 1.1,
  label,
  bg,
}: SwipaLoaderProps) {
  // Unique animation-name so multiple loaders don't share keyframe state
  const id = useId().replace(/:/g, '');
  const animName = `swTrail_${id}`;

  const trail = (
    <>
      <style>{`
        @keyframes ${animName} {
          0%   { opacity: 0.12; transform: translateY(6px); }
          30%  { opacity: 1;    transform: translateY(0); }
          60%  { opacity: 0.6;  transform: translateY(-2px); }
          100% { opacity: 0.12; transform: translateY(-8px); }
        }
      `}</style>
      <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-label={label ?? 'loading'}>
        {[0, 1, 2].map((i) => (
          <g
            key={i}
            style={{
              transformOrigin: '60px 60px',
              animation: `${animName} ${speed}s ease-in-out ${i * 0.18}s infinite`,
            }}
          >
            <path
              d={`M32 ${40 + i * 24} L 60 ${20 + i * 24} L 88 ${40 + i * 24}`}
              stroke={color}
              strokeWidth={11}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        ))}
      </svg>
    </>
  );

  if (mode === 'splash') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(180deg, #7A1F2B 0%, #5A1421 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 32,
            color: '#fff',
          }}
        >
          <SwipaLoader mode="inline" size={120} color="#fff" speed={speed} />
          {/* Wordmark: s W↗♥ i p a — heart perched above the W junction. */}
          <div
            style={{
              fontFamily: '"Quicksand","Nunito",system-ui,sans-serif',
              fontWeight: 700,
              fontSize: 44,
              letterSpacing: '-0.04em',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'baseline',
              color: '#fff',
            }}
          >
            <span>s</span>
            <span style={{ position: 'relative', display: 'inline-block' }}>
              <span>V</span>
              <span style={{ marginLeft: -14.5 }}>V</span>
              <svg
                width={13}
                height={13}
                viewBox="0 0 24 24"
                fill="#C5243A"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: -2,
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                }}
                aria-hidden="true"
              >
                <path d="M12 21s-7.5-4.5-9.5-10C1.5 7.5 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 22.5 7.5 21.5 11c-2 5.5-9.5 10-9.5 10z" />
              </svg>
            </span>
            <span>ipa</span>
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: '8vh',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: 11,
              letterSpacing: '0.35em',
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            живи свайпом
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'overlay') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(10,10,10,0.72)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        {trail}
        {label && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.05em',
            }}
          >
            {label}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'bubble') {
    // For embedding inside a chat bubble (media upload/receive).
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: bg ?? 'transparent',
          borderRadius: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {trail}
      </div>
    );
  }

  return trail;
}

export default SwipaLoader;
